import { useEffect, useSyncExternalStore } from 'react';
import { veniceBalance, type VeniceBalance } from '../utils/venice';

/**
 * The Venice balance, read once for the whole page: every node showing it and the settings
 * share one request. A read less than a minute old is reused; after a generation, callers
 * ask for a fresh one.
 */
interface BalanceState {
    key: string | null;
    balance: VeniceBalance | null;
    error: string | null;
    loading: boolean;
    readAt: number;
}

const FRESH_FOR_MS = 60_000;
let state: BalanceState = { key: null, balance: null, error: null, loading: false, readAt: 0 };
const listeners = new Set<() => void>();
let inFlight: Promise<void> | null = null;
let readAgain = false;

const set = (patch: Partial<BalanceState>) => {
    state = { ...state, ...patch };
    listeners.forEach(listener => listener());
};

const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
};

/** Reads the balance, unless a read for this key is fresh; `force` reads again (after a generation). */
export const refreshVeniceBalance = (apiKey: string | undefined, force = false): Promise<void> => {
    if (!apiKey) {
        if (state.key !== null) set({ key: null, balance: null, error: null, loading: false, readAt: 0 });
        return Promise.resolve();
    }
    if (state.key !== apiKey) set({ key: apiKey, balance: null, error: null, loading: false, readAt: 0 });
    if (inFlight) {
        // A read under way may have started before the charge: read once more after it
        if (force) readAgain = true;
        return inFlight;
    }
    if (!force && !state.error && state.readAt > 0 && Date.now() - state.readAt < FRESH_FOR_MS) return Promise.resolve();
    set({ loading: true });
    inFlight = veniceBalance(apiKey).then(
        (balance) => { if (state.key === apiKey) set({ balance, error: null, loading: false, readAt: Date.now() }); },
        (e: unknown) => {
            if (state.key === apiKey) set({ error: (e as { message?: string })?.message || 'Could not read the balance.', loading: false, readAt: Date.now() });
        },
    ).finally(() => {
        inFlight = null;
        if (readAgain) {
            readAgain = false;
            void refreshVeniceBalance(apiKey, true);
        }
    });
    return inFlight;
};

export interface VeniceBalanceView {
    balance: VeniceBalance | null;
    error: string | null;
    loading: boolean;
    refresh: () => void;
}

export const useVeniceBalance = (apiKey: string | undefined): VeniceBalanceView => {
    const current = useSyncExternalStore(subscribe, () => state);
    useEffect(() => { void refreshVeniceBalance(apiKey); }, [apiKey]);
    const mine = !!apiKey && current.key === apiKey;
    return {
        balance: mine ? current.balance : null,
        error: mine ? current.error : null,
        loading: mine && current.loading,
        refresh: () => { void refreshVeniceBalance(apiKey, true); },
    };
};

/** For tests: forget what was read. */
export const resetVeniceBalance = (): void => {
    state = { key: null, balance: null, error: null, loading: false, readAt: 0 };
    inFlight = null;
    readAgain = false;
};
